"use client";

import axios from "axios";
import { Link } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";
import { usePurchaseOrder, usePurchaseOrderMutations } from "@/hooks/api/usePurchaseOrders";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";

export default function PurchaseOrderDetailPage() {
  const params = useParams();
  const id = Number(params.id);
  const navigate = useNavigate();
  const { data: po, isLoading } = usePurchaseOrder(Number.isFinite(id) ? id : null);
  const { approve, send, convert } = usePurchaseOrderMutations();
  const sendingOnce = po?.status === "sent";
  const supplierEmail = (po as unknown as { supplier_email?: string | null } | null)?.supplier_email ?? null;
  const statusLabel = po?.status === "sent" ? "placed" : po?.status ?? "";
  const grnId = (po as unknown as { grn_id?: number | null } | null)?.grn_id ?? null;
  const grnStatus = (po as unknown as { grn_status?: string | null } | null)?.grn_status ?? null;

  if (!Number.isFinite(id)) {
    return <p className="p-4 text-sm text-destructive">Invalid id</p>;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <Link
        to="/purchase/orders"
        className={cn(buttonVariants({ variant: "outline" }), "inline-flex h-9 items-center px-3")}
      >
        ← Orders
      </Link>
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {po && (
        <>
          <div>
            <h1 className="text-xl font-semibold font-mono">{po.po_no}</h1>
            <p className="text-sm text-muted-foreground">
              {po.po_date} · Status: {statusLabel} · ₹{Number(po.total_amount).toLocaleString("en-IN")}
            </p>
            {po.status === "sent" && (
              <p className="mt-1 text-sm text-emerald-700">
                Order placed. Email sent{supplierEmail ? ` to ${supplierEmail}` : ""}.
              </p>
            )}
            {grnId ? (
              <p className="mt-1 text-sm text-muted-foreground">
                GRN:{" "}
                <Link className="font-mono text-primary hover:underline" to={`/purchase/grn/${grnId}`}>
                  #{grnId}
                </Link>
                {grnStatus ? ` · ${grnStatus}` : ""}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {po.status === "draft" || po.status === "sent" ? (
              <Button disabled={approve.isPending} onClick={() => void approve.mutateAsync(po.id)}>
                Approve
              </Button>
            ) : null}
            <Button
              variant="secondary"
              disabled={send.isPending || sendingOnce}
              onClick={async () => {
                try {
                  await send.mutateAsync(po.id);
                } catch (e: unknown) {
                  if (axios.isAxiosError(e) && e.response?.status === 409) {
                    // already sent; ignore
                    return;
                  }
                  throw e;
                }
              }}
              title={sendingOnce ? "Already sent" : undefined}
            >
              Email PDF
            </Button>
            {po.status === "approved" ? (
              <Button
                variant="outline"
                disabled={convert.isPending}
                onClick={async () => {
                  const grn = await convert.mutateAsync(po.id);
                  if (grn?.id) {
                    navigate(`/purchase/grn/${grn.id}`);
                  }
                }}
              >
                Convert to GRN
              </Button>
            ) : null}
          </div>
          <Card className="p-4">
            <h2 className="mb-2 font-medium">Items</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-1">Product</th>
                  <th>Qty</th>
                  <th>Rate</th>
                  <th>Recv</th>
                  <th className="text-right">Amt</th>
                </tr>
              </thead>
              <tbody>
                {po.items?.map((it) => (
                  <tr key={it.id} className="border-b border-border/60">
                    <td className="py-2">{it.product_name}</td>
                    <td>{it.qty_ordered}</td>
                    <td>{it.rate}</td>
                    <td>{it.qty_received}</td>
                    <td className="text-right">{it.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
